//Feb21 FieldEditContext.js:

import React, { createContext, useContext, useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useFormContext } from './FormContext';
import { updateFieldInFirestore, getFieldMetadataForSingleField } from '../utils/firestore';
import { v4 as uuidv4 } from 'uuid';
import { useFieldMetadata } from './FieldMetadataContext';
import { safeGetItem, safeSetItem } from '../utils/storageUtils';

const FieldEditContext = createContext();

const debounce = (func, delay) => {
  let timer;
  return (...args) => {
    if (timer) clearTimeout(timer);
    return new Promise((resolve, reject) => {
      timer = setTimeout(async () => {
        try {
          const result = await func(...args);
          resolve(result);
        } catch (error) {
          reject(error);
        }
      }, delay);
    });
  };
};


export const FieldEditProvider = ({ children }) => {
  const { formId } = useFormContext();
  // Log formId to verify if it's being retrieved correctly from FormContext
//  console.log(`[FieldEditProvider] Retrieved formId from FormContext: ${formId}`);

  // Memoize makeKey to prevent re-computation
  const makeKey = useMemo(() => {
    if (!formId) return null; // Handle the case when formId is not available
    return (key) => `form_${formId}_field_${key}`;
  }, [formId]);
//  const makeKey = useCallback((key) => `form_${formId}_field_${key}`, [formId]);


  // Initialize state after all functions are defined
const { fieldsMetadata, updateFieldMetadata, addFieldMetadata, deleteFieldMetadata } = useFieldMetadata();

// ────────────────────────────────────────────────────────────────
// Publish-compat metadata mirroring:
// FieldMetadataContext now uses mode-aware keys, but publish pipeline
// still reads legacy `form_${formId}_fieldsMetadata`. Mirror writes so
// publishing always captures the latest metadata edits.
// ────────────────────────────────────────────────────────────────
const getEffectiveViewMode = useCallback(() => {
  try {
    const isBuilder =
      (typeof window !== 'undefined') &&
      window.location.pathname.startsWith('/form-builder');

    if (!isBuilder) return 'published';

    const raw = sessionStorage.getItem(`form_${formId}_layoutVersionMode`);
    const parsed = raw ? JSON.parse(raw) : null;
    return parsed?.value || 'auto'; // 'auto' | 'published' | 'draftOverlay'
  } catch (e) {
    return 'published';
  }
}, [formId]);

const mirrorMetadataToLegacyKey = useCallback((fieldId, patch) => {
  if (!formId || !fieldId || !patch || typeof patch !== 'object') return;

  try {
    const mode = getEffectiveViewMode();
    const modeKey = `form_${formId}_fieldsMetadata__${mode}`;
    const legacyKey = `form_${formId}_fieldsMetadata`;

    const base =
      safeGetItem(modeKey) ||
      safeGetItem(legacyKey) ||
      {};

    const next = {
      ...(base || {}),
      [fieldId]: {
        ...(base?.[fieldId] || {}),
        ...patch,
      },
    };

    // Keep both in sync so publish always has the latest
    safeSetItem(modeKey, next);
    safeSetItem(legacyKey, next);
  } catch (e) {
    // non-fatal
  }
}, [formId, getEffectiveViewMode]);

  const [isLoading, setIsLoading] = useState(false); // State to indicate loading operations
  const [error, setError] = useState(null); // State to store error messages
  const [isFieldChangesReady, setIsFieldChangesReady] = useState(false); // Track if field changes are ready
  const [validationErrors, setValidationErrors] = useState({});
  const [selectedFieldForEdit, setSelectedFieldForEdit] = useState(null);
  const previousFormId = useRef(formId);
  const [fieldChanges, setFieldChanges] = useState({});
//latestchanges
    const [originalFieldMetadata, setOriginalFieldMetadata] = useState(null); // Move inside the component

  // Effect to log current fieldChanges
  useEffect(() => {
    if (!formId) return; // Prevent execution if formId is not available

//    console.log("Current fieldChanges state:", fieldChanges);
    setIsFieldChangesReady(true); // Mark as ready when fieldChanges are set
  }, [formId, fieldChanges]);


  useEffect(() => {
    if (!formId || !isFieldChangesReady) return; // Prevent execution if formId is not available or changes aren't ready

    const applyChangesToLocalState = () => {
      Object.keys(fieldChanges).forEach(fieldId => {
        // Log field changes being applied
//        console.log(`[FieldEditContext] Applying changes to field ${fieldId}:`, fieldChanges[fieldId]);
      });
    };
    
    // Only apply changes if field changes are ready
    if (isFieldChangesReady) {
      applyChangesToLocalState();
    }
  }, [formId, isFieldChangesReady, fieldChanges]);

  // Reset changes when form ID changes (simplified to remove session/local storage handling)
useEffect(() => {
  if (previousFormId.current !== formId && formId && previousFormId.current !== null) {
    // Reset field changes only if transitioning between valid form IDs
//    console.log(`[FieldEditContext] Resetting field changes due to formId change from ${previousFormId.current} to ${formId}`);

    setFieldChanges({});
    setIsFieldChangesReady(false); // Reset readiness state when form ID changes

    // Update previousFormId after handling changes
    previousFormId.current = formId;
  } else {
    // Just update the formId tracking if it's the first load
    previousFormId.current = formId;
  }
}, [formId]);




useEffect(() => {
  if (!formId || !selectedFieldForEdit) return;

  // Only clear selection after metadata is known to be loaded
  if (selectedFieldForEdit && !fieldsMetadata[selectedFieldForEdit.id] && Object.keys(fieldsMetadata || {}).length > 0) {
    console.warn(`[FieldEditContext] Metadata missing for fieldId: ${selectedFieldForEdit.id}. Clearing selectedFieldForEdit.`);
    setSelectedFieldForEdit(null);
    setError(`Field ${selectedFieldForEdit.id} has been deleted.`);
  }
}, [formId, fieldsMetadata, selectedFieldForEdit]);



// Function to update a specific validation error and optionally its external helper text
const updateValidationError = useCallback((fieldId, errorMessage, externalHelperText = null) => {
  setValidationErrors((prevErrors) => ({
    ...prevErrors,
    [fieldId]: errorMessage ? { errorMessage, externalHelperText } : null,
  }));

//  console.log(`[FieldEditContext] Validation error updated for field: ${fieldId} with message: ${errorMessage}`);
}, []);

  // Clear validation errors when starting a new form submission
const clearValidationErrors = useCallback(() => {
  setValidationErrors({});
}, []);



const clearSelectedField = useCallback(() => {
//  console.log("[FieldEditContext: settings] clearSelectedField called. Setting selectedFieldForEdit to null.");
  setSelectedFieldForEdit(null);
}, []);


const refreshFieldMetadata = async (fieldId) => {
  if (!fieldId) return null; // Defensive check

  // Check if metadata is already in FieldMetadataContext
  const existingMetadata = fieldsMetadata[fieldId];
  if (existingMetadata) {
    // Metadata exists, no need to fetch it again
    return existingMetadata;
  }

  console.warn(`[FieldEditContext] Metadata not found in FieldMetadataContext for field ${fieldId}.`);
  // At this point, you could log the issue or attempt some form of recovery, but fetching should always be handled by FieldMetadataContext.
  
  return null;  // Return null to indicate no metadata found or fetched
};



const updateField = useCallback((fieldId, fieldUpdates) => {
//  console.log("[FieldEditContext: debug] Updating field:", fieldId, "with updates:", fieldUpdates);
  /* ─── LOG‑2a ────────────────────────────────────────────── */
  if (fieldUpdates.helperText !== undefined) {
//    console.log(
//      '[FieldEditContext: LOG‑2a] helperText received in updateField for',
//      fieldId,
//      '→',
//      fieldUpdates.helperText
//    );
  }
  if (fieldUpdates.required !== undefined) {
//    console.log(
//      '[FieldEditContext: LOG‑2a] required flag received in updateField for',
//      fieldId,
//      '→',
//      fieldUpdates.required
//    );
  }
  /* ───────────────────────────────────────────────────────── */
  // Dynamically ensure that numerical fields are treated as numbers
  const numericalFields = ['maxLength', 'minValue', 'maxValue']; // Extend this array with any new numerical fields
  Object.keys(fieldUpdates).forEach(key => {
    if (numericalFields.includes(key) && typeof fieldUpdates[key] === 'string') {
      fieldUpdates[key] = Number(fieldUpdates[key]); // Convert string numbers to actual numbers
    }
  });

  // Handle image-specific fields
  // Step 2: Handle image-specific field logic
  if (fieldUpdates.type === 'image') {
//    console.log(`[FieldEditContext: debug] Field ${fieldId} is of type "image". Evaluating image-specific metadata...`);

    if (
      Object.prototype.hasOwnProperty.call(fieldUpdates, 'allowedImageTypes') &&
      fieldUpdates.allowedImageTypes !== null &&
      !Array.isArray(fieldUpdates.allowedImageTypes)
    ) {
      console.error(`[FieldEditContext: error] allowedImageTypes is not an array for image field ${fieldId}:`, fieldUpdates.allowedImageTypes);
      throw new Error("allowedImageTypes must be an array.");
    }

    const imageMetadataKeys = [
      'assetReference',
      'allowedImageTypes',
      'thumbnail',
      'thumb',
      'thumbUrl',
      'versions',
      'dimensions',
      'assetType',
      'systemAssetType',
    ];

    const imageUpdates = imageMetadataKeys.reduce((acc, key) => {
      if (Object.prototype.hasOwnProperty.call(fieldUpdates, key)) {
        acc[key] = fieldUpdates[key];
      }

      return acc;
    }, {});

    if (Object.keys(imageUpdates).length > 0) {
//      console.log(`[FieldEditContext: debug] Applying image updates to FieldMetadataContext for field ${fieldId}:`, imageUpdates);
      updateFieldMetadata(fieldId, imageUpdates);
    }
  }


// Update fieldChanges state only if actual changes are made
setFieldChanges((prev) => {
    // ✅ fieldId is the canonical edit-buffer key (UI instance). This keeps submission fallback safe.
    const currentFieldChanges = prev[fieldId] || {};
    const updatedChanges = { ...currentFieldChanges, ...fieldUpdates };

  /* ─── LOG-3 ─────────────────────────────────────────────── */
  const fieldUFI = fieldsMetadata[fieldId]?.ufi || fieldId;
//  console.log(
//    '[FieldEditContext: LOG-3] fieldChanges pending for',
//    fieldUFI,
//    '→ helperText:',
//    updatedChanges.helperText,
//    '| required:',
//    updatedChanges.required
//  );
  /* ───────────────────────────────────────────────────────── */

//    console.log(`[FieldEditContext: debug] Before update - fieldChanges:`, prev);
//    console.log(`[FieldEditContext: debug] After update - New fieldChanges:`, updatedChanges);

    // Only update state if actual changes were made
    if (JSON.stringify(currentFieldChanges) === JSON.stringify(updatedChanges)) {
//        console.log(`[FieldEditContext: debug] No changes detected for field ${fieldId}. Skipping update.`);
        return prev;
    }

//    console.log("[FieldEditContext: debug] Field changes updated for:", fieldId, updatedChanges);

    const newState = {
        ...prev,
        [fieldId]: updatedChanges, // ✅ Store updates using fieldId only
    };

    // ✅ **OPTIMIZED DEPENDENCY LOGIC - Update only the relevant dependent field**
    const dependentFieldId = Object.keys(fieldsMetadata).find(
        key => fieldsMetadata[key]?.ufi === fieldsMetadata[fieldId]?.disabledDependentFieldId
    );

    if (dependentFieldId) {
        const dependentFieldUFI = fieldsMetadata[dependentFieldId]?.ufi;
        const dependentFieldValue = updatedChanges.defaultValue || '';
        const shouldDisable = !dependentFieldValue;

//        console.log(`[FieldEditContext: debug] Checking dependent field ${dependentFieldId} (UFI: ${dependentFieldUFI}) - Current Value:`, dependentFieldValue);
//        console.log(`[FieldEditContext: debug] Updating disabled state for dependent field ${dependentFieldId} (UFI: ${dependentFieldUFI}):`, shouldDisable);

        // ✅ store dependent updates by fieldId as well
        newState[dependentFieldId] = {
            ...(prev[dependentFieldId] || {}),
            disabled: shouldDisable,
        };
    }

    return newState;
});


  /* ────────────────────────────────────────────────
   *  Validation should only run when the incoming
   *  patch actually *changes* the user-entered value.
   * ──────────────────────────────────────────────── */
  const isRequired = fieldsMetadata[fieldId]?.required;

  // 1)  Figure out whether this patch alters the stored answer
  const prevStored   = fieldsMetadata[fieldId]?.defaultValue ?? '';
  const patchHasNew  =
        ('defaultValue' in fieldUpdates && fieldUpdates.defaultValue !== prevStored) ||
        ('value'        in fieldUpdates);           // live-form uses .value

  // 2)  Skip validation when we’re just changing metadata (e.g. required flag)
  if (!patchHasNew) {
//    console.log('[FieldEditContext] metadata-only patch – skip validation for', fieldId);
    //  Ensure publish sees metadata edits (label/required/etc.)
    // FieldMetadataContext writes mode-aware keys; publish reads legacy key.
    mirrorMetadataToLegacyKey(fieldId, fieldUpdates);

    // Best-practice safe version:
    // Only push a metadata-only patch into FieldMetadataContext when it affects
    // runtime behavior (so preview + submit-time logic stay aligned).
    const RUNTIME_META_KEYS = new Set([
      // "Other" runtime behavior
      'allowOther',
      'includeOther',
      'otherHint',
      'otherHelperText',
      'otherErrorText',
      'otherMaxLength',

      // Multi-select runtime behavior (bulk vs single record)
      'multiSelectResultFormat',
    ]);

    const touchesRuntimeMeta =
      fieldUpdates &&
      typeof fieldUpdates === 'object' &&
      Object.keys(fieldUpdates).some((k) => RUNTIME_META_KEYS.has(k));

    if (touchesRuntimeMeta) {
      updateFieldMetadata(fieldId, fieldUpdates); //  push into canonical metadata state (safe-scoped)
    }
  } else {
    const newValue = fieldUpdates.defaultValue ?? fieldUpdates.value ?? '';

    if (isRequired) {
//      console.log(
//        '[FieldEditContext:LOG-2b] validation check – field',
//        fieldId, '| required:', isRequired, '| newValue:', `"${newValue}"`
//      );
      if (!newValue.trim()) {
        updateValidationError(fieldId, 'This field is required.');
//        console.log(`[FieldEditContext] Validation error set for required field ${fieldId}.`);
      } else {
        updateValidationError(fieldId, null);
//        console.log(`[FieldEditContext] Validation error cleared for required field ${fieldId}.`);
      }
    } else if (validationErrors[fieldId]) {
      // not required but had an error → clear it
      updateValidationError(fieldId, null);
//      console.log(`[FieldEditContext] Validation error cleared for non-required field ${fieldId}.`);
    }
  }



// Update selectedFieldForEdit metadata if required
if (selectedFieldForEdit && selectedFieldForEdit.id === fieldId) {
  setSelectedFieldForEdit((prev) => {
    // Guard if selection was cleared mid-update
    if (!prev || prev.id !== fieldId) return prev;
    const prevMeta = prev.metadata || {};
    const updatedMetadata = { ...prevMeta, ...fieldUpdates };
    if (JSON.stringify(prevMeta) !== JSON.stringify(updatedMetadata)) {
      return { ...prev, metadata: updatedMetadata };
    }
    return prev;
  });
}

}, [
  selectedFieldForEdit,
  validationErrors,
  updateValidationError,
  fieldsMetadata,
  updateFieldMetadata,
  mirrorMetadataToLegacyKey
]);


const handleFieldChange = useCallback((fieldId, changes) => {
//  console.log("[FieldEditContext: debug] handleFieldChange called for field:", fieldId, "with changes:", changes);
  setFieldChanges((prev) => {
    const currentFieldChanges = prev[fieldId] || {};
    const updatedChanges = { ...currentFieldChanges, ...changes };
//    console.log("[FieldEditContext: debug] Updated fieldChanges for field", fieldId, ":", updatedChanges);
    return {
      ...prev,
      [fieldId]: updatedChanges,
    };
  });
}, []);

// Additionally, add a useEffect to log whenever fieldChanges updates:
//useEffect(() => {
//  console.log("[FieldEditContext: useEffect] fieldChanges state updated:", fieldChanges);
//}, [fieldChanges]);

//end of new code

const selectFieldForEdit = debounce(async (fieldId) => {
  if (!fieldId) return;

//  console.log(`[FieldEditContext] Selecting field ${fieldId} for editing.`);

  // Prevent re-selecting the field if it's already selected
  if (selectedFieldForEdit && selectedFieldForEdit.id === fieldId) {
//    console.log(`[FieldEditContext] Field ${fieldId} is already selected.`);
    return;
  }

  setIsLoading(true);

  // Check if metadata has been received from FieldMetadataContext
  let metadata = fieldsMetadata[fieldId];
//  console.log(`[FieldEditContext] Retrieved metadata from FieldMetadataContext for field ${fieldId}:`, metadata);

  if (!metadata) {
    console.warn(`[FieldEditContext] No metadata found for field ${fieldId}.`);
    setIsLoading(false);
    return;
  }

//  console.log(`[FieldEditContext] Original metadata for field ${fieldId}:`, metadata);

  // Save the original metadata when selecting a field for edit
  setOriginalFieldMetadata(metadata); // This is the key step to ensure we compare against the original state

  const existingChanges = fieldChanges[fieldId] || {};
  const combinedMetadata = { ...metadata, ...existingChanges };

//  console.log(`[FieldEditContext] Combined metadata for field ${fieldId}:`, combinedMetadata);

  // Check if there are any actual changes to the selected field metadata
  const currentSelectedMetadata = selectedFieldForEdit ? selectedFieldForEdit.metadata : {};
  const hasChanges = Object.keys(combinedMetadata).some(key => combinedMetadata[key] !== currentSelectedMetadata[key]);

  if (hasChanges) {
//    console.log(`[FieldEditContext] Updating selected field with new metadata for field ${fieldId}.`);
      setSelectedFieldForEdit({ id: fieldId, metadata: combinedMetadata });
//      console.log(`[FieldEditContext] setSelectedFieldForEdit called with:`, { id: fieldId, metadata: combinedMetadata }, `at ${new Date().toISOString()}`);

  } else {
//    console.log(`[FieldEditContext] No changes detected for field ${fieldId}. Skipping re-selection.`);
  }

  setIsLoading(false);
}, 300);


/**
 * selectFieldForEditImmediate
 * -----------------------------------------------------------------------------
 * Immediate field-selection path for explicit spreadsheet side-panel actions.
 *
 * WHY THIS EXISTS
 *  • FormListsLab dependency/settings clicks are intentional user actions.
 *  • Waiting for the existing 300ms debounced selectFieldForEdit(...) makes
 *    the V2 spreadsheet sidepanel feel delayed.
 *  • The existing debounced selector is preserved for the rest of the form
 *    builder so Design/Layout behavior does not change.
 *
 * IMPORTANT
 *  • This does not write Firestore.
 *  • This does not update fieldsMetadata directly.
 *  • This does not change Save Draft / Publish behavior.
 *  • This only prepares selectedFieldForEdit for the existing FieldSettings UI.
 *  • Keep the metadata merge behavior aligned with selectFieldForEdit(...).
 */
const selectFieldForEditImmediate = useCallback(async (fieldId) => {
  if (!fieldId) return;

//  console.log(`[FieldEditContext] Selecting field ${fieldId} for editing.`);

  // Prevent re-selecting the field if it's already selected
  if (selectedFieldForEdit && selectedFieldForEdit.id === fieldId) {
//    console.log(`[FieldEditContext] Field ${fieldId} is already selected.`);
    return;
  }

  setIsLoading(true);

  // Check if metadata has been received from FieldMetadataContext
  let metadata = fieldsMetadata[fieldId];
//  console.log(`[FieldEditContext] Retrieved metadata from FieldMetadataContext for field ${fieldId}:`, metadata);

  if (!metadata) {
    console.warn(`[FieldEditContext] No metadata found for field ${fieldId}.`);
    setIsLoading(false);
    return;
  }

//  console.log(`[FieldEditContext] Original metadata for field ${fieldId}:`, metadata);

  // Save the original metadata when selecting a field for edit
  setOriginalFieldMetadata(metadata); // This is the key step to ensure we compare against the original state

  const existingChanges = fieldChanges[fieldId] || {};
  const combinedMetadata = { ...metadata, ...existingChanges };

//  console.log(`[FieldEditContext] Combined metadata for field ${fieldId}:`, combinedMetadata);

  // Check if there are any actual changes to the selected field metadata
  const currentSelectedMetadata = selectedFieldForEdit ? selectedFieldForEdit.metadata : {};
  const hasChanges = Object.keys(combinedMetadata).some(key => combinedMetadata[key] !== currentSelectedMetadata[key]);

  if (hasChanges) {
//    console.log(`[FieldEditContext] Updating selected field with new metadata for field ${fieldId}.`);
      setSelectedFieldForEdit({ id: fieldId, metadata: combinedMetadata });
//      console.log(`[FieldEditContext] setSelectedFieldForEdit called with:`, { id: fieldId, metadata: combinedMetadata }, `at ${new Date().toISOString()}`);

  } else {
//    console.log(`[FieldEditContext] No changes detected for field ${fieldId}. Skipping re-selection.`);
  }

  setIsLoading(false);
}, [
  selectedFieldForEdit,
  fieldsMetadata,
  fieldChanges,
]);



//changed
const saveFieldChangesLocally = useCallback((fieldId, fieldUpdates) => {
  setFieldChanges((prev) => {
    const currentFieldChanges = prev[fieldId] || {};

    // Check if there are actual changes in the updates
    const hasChanges = Object.keys(fieldUpdates).some(
      (key) => currentFieldChanges[key] !== fieldUpdates[key]
    );

    if (!hasChanges) {
//      console.log(`[FieldEditContext] No changes detected for field ${fieldId}. Skipping local save.`);
      return prev; // Return previous state if no changes detected
    }

    // If there are changes, proceed with updating fieldChanges
//    console.log(`[FieldEditContext] Saving local changes for field ${fieldId}.`, fieldUpdates);
    return {
      ...prev,
      [fieldId]: { ...currentFieldChanges, ...fieldUpdates },
    };
  });
}, []);

//latestchanges
const mergeFieldChangesToMetadata = (fieldId, fieldUpdates) => {
  const currentMetadata = fieldsMetadata[fieldId] || {};
  const originalMetadata = originalFieldMetadata || {}; // Compare against the original metadata

//  console.log(`[FieldEditContext] Merging updates for field ${fieldId}`);

  // Compare field updates with the original metadata
  const hasChangesComparedToOriginal = Object.keys(fieldUpdates).some(
    (key) => originalMetadata[key] !== fieldUpdates[key]
  );

  // Compare the current metadata with the field updates
  const hasChangesComparedToCurrent = Object.keys(fieldUpdates).some(
    (key) => currentMetadata[key] !== fieldUpdates[key]
  );

  // Only update if there are changes compared to either original or current metadata
  if (hasChangesComparedToOriginal || hasChangesComparedToCurrent) {
//    console.log(`[FieldEditContext] Actual changes detected for field ${fieldId}. Merging into FieldMetadataContext.`);
    updateFieldMetadata(fieldId, fieldUpdates); // Push updates to FieldMetadataContext
  } else {
//    console.log(`[FieldEditContext] No changes detected for field ${fieldId}.`);
  }
};




const saveFieldChanges = useCallback(async () => {
  if (selectedFieldForEdit && fieldChanges[selectedFieldForEdit.id]) {
    const fieldId = selectedFieldForEdit.id;
    const changes = fieldChanges[fieldId];

    // Check if there are any actual changes to save
    const currentMetadata = fieldsMetadata[fieldId] || {};
    const hasChanges = Object.keys(changes).some(key => currentMetadata[key] !== changes[key]);

    if (!hasChanges) {
//      console.log(`[FieldEditContext] No changes detected for field ${fieldId}. Skipping save.`);
      return; // Exit early if no changes
    }

    setIsLoading(true);

    try {
      // 1. Merge changes into FieldMetadataContext via mergeFieldChangesToMetadata
//      console.log(`[FieldEditContext] Saving changes for field ${fieldId}.`);
      mergeFieldChangesToMetadata(fieldId, changes);  // Step 1: Merge field changes into metadata

      // 2. Optionally, update Firestore, but only if FieldMetadataContext is responsible for Firestore
      // If FieldMetadataContext is responsible for saving to Firestore, remove this call
//      await updateFieldInFirestore(fieldId, changes); // (Optional: Remove if FieldMetadataContext manages this)

      // 3. Clear the fieldChanges for the field after successful save
      setFieldChanges((prev) => {
        const updatedChanges = { ...prev };
        delete updatedChanges[fieldId]; // Remove the field from local changes
        return updatedChanges;
      });

      // 4. Optionally, refresh field metadata from FieldMetadataContext to ensure it reflects the latest state
      await refreshFieldMetadata(fieldId);

      setIsLoading(false);
    } catch (error) {
      console.error('Error saving field changes:', error);
      setError(`Error saving field changes: ${error.message}`);
      setIsLoading(false);
    }
  }
}, [selectedFieldForEdit, fieldChanges, mergeFieldChangesToMetadata, updateFieldMetadata, refreshFieldMetadata]);


  return (
    <FieldEditContext.Provider value={{
      selectedFieldForEdit,
      selectFieldForEdit,
      selectFieldForEditImmediate,
      updateField,
      handleFieldChange,
      saveFieldChangesLocally,
      saveFieldChanges,
      clearSelectedField,
      isLoading,
      setIsLoading,
      error,
      setError,
      fieldChanges,
      setFieldChanges,
      validationErrors,
      updateValidationError,
      clearValidationErrors,
      isFieldChangesReady,
      mergeFieldChangesToMetadata, 
    }}>
      {children}
    </FieldEditContext.Provider>
  );
};

export const useFieldEdit = () => useContext(FieldEditContext);
